// Lightweight client-side cache for vault UI panels.
// Pure in-memory module-level store. No I/O at import time.

type VaultId = string;
type OrgId = string | null | undefined;

export interface CachedVaultDetails {
  id: VaultId;
  slug?: string | null;
  organizationUrlSlug?: string | null;
  visibility?: 'private' | 'protected' | 'org' | 'public';
  repoUrl?: string | null;
  mode?: string | null;
  detectedSourceWiki?: string | null;
  updatedAt: number;
}

export interface CachedMembershipSnapshot {
  entries: any[];
  allowlists: any[];
  updatedAt: number;
}

const vaultDetailsCache = new Map<VaultId, CachedVaultDetails>();
const membershipCache = new Map<string, CachedMembershipSnapshot>();

function membershipKey(vaultId?: VaultId | null, organizationId?: OrgId): string {
  return `${vaultId ?? 'none'}|${organizationId ?? 'none'}`;
}

// Vault details cache
export function setCachedVaultDetails(vaultId: VaultId, details: Partial<CachedVaultDetails>): void {
  const prev = vaultDetailsCache.get(vaultId) ?? { id: vaultId, updatedAt: 0 };
  const next: CachedVaultDetails = {
    ...prev,
    ...details,
    id: vaultId,
    updatedAt: Date.now(),
  };
  vaultDetailsCache.set(vaultId, next);
}

export function getCachedVaultDetails(vaultId?: VaultId | null): CachedVaultDetails | undefined {
  if (!vaultId) return undefined;
  return vaultDetailsCache.get(vaultId);
}

// Membership snapshot cache
export function setCachedMembershipSnapshot(
  vaultId?: VaultId | null,
  organizationId?: OrgId,
  snapshot?: { entries: any[]; allowlists: any[] },
): void {
  const key = membershipKey(vaultId, organizationId);
  if (!snapshot) {
    membershipCache.delete(key);
    return;
  }
  membershipCache.set(key, {
    entries: Array.isArray(snapshot.entries) ? snapshot.entries : [],
    allowlists: Array.isArray(snapshot.allowlists) ? snapshot.allowlists : [],
    updatedAt: Date.now(),
  });
}

export function getCachedMembershipSnapshot(
  vaultId?: VaultId | null,
  organizationId?: OrgId,
): CachedMembershipSnapshot | undefined {
  const key = membershipKey(vaultId, organizationId);
  return membershipCache.get(key);
}

export function hasMembershipCache(vaultId?: VaultId | null, organizationId?: OrgId): boolean {
  const key = membershipKey(vaultId, organizationId);
  return membershipCache.has(key);
}

